// src/WalletProvider.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { nativeEmailWalletAdapter } from "./utils/nativeEmailWalletAdapter";

require("@solana/wallet-adapter-react-ui/styles.css");

const WalletContextProvider = ({ children }) => {
    const wallets = useMemo(() => [nativeEmailWalletAdapter], []);
    const [emailSessionReady, setEmailSessionReady] = useState(nativeEmailWalletAdapter.hasSession);

    useEffect(() => {
        let active = true;
        const handleSessionChange = (ready) => {
            if (active) setEmailSessionReady(ready);
        };
        nativeEmailWalletAdapter.on("sessionChange", handleSessionChange);
        nativeEmailWalletAdapter.restoreSession().catch((error) => {
            console.warn("Email wallet session restoration failed:", error);
        });
        return () => {
            active = false;
            nativeEmailWalletAdapter.off("sessionChange", handleSessionChange);
        };
    }, []);

    // The provider connects after installing its listeners. Other wallets keep
    // their existing manual-connect behavior.
    const autoConnectEmailWallet = useCallback(
        async (adapter) => adapter === nativeEmailWalletAdapter && adapter.hasSession,
        []
    );
    const endpoint = process.env.REACT_APP_RPC_ENDPOINT;
    const handleWalletError = (error, adapter) => {
        const cause = error?.cause || error?.error || error;
        const phantomProvider =
            typeof window !== "undefined"
                ? window.phantom?.solana || null
                : null;
        const phantomPublicKey =
            phantomProvider?.publicKey?.toBase58?.() || "";

        console.error("Wallet connection error", {
            wallet: adapter?.name,
            standardAdapter: !!adapter?.standard,
            name: error?.name,
            message: error?.message,
            code: cause?.code,
            causeMessage: cause?.message,
            phantomProvider: adapter?.name?.startsWith?.("Phantom")
                ? {
                    detected: !!phantomProvider,
                    isPhantom: !!phantomProvider?.isPhantom,
                    isConnected: !!phantomProvider?.isConnected,
                    hasPublicKey: !!phantomPublicKey,
                    publicKeyPreview: phantomPublicKey
                        ? `${phantomPublicKey.slice(0, 4)}...${phantomPublicKey.slice(-4)}`
                        : "",
                }
                : undefined,
            error,
        });
    };

    return (
        <ConnectionProvider endpoint={endpoint}>
            <WalletProvider wallets={wallets} autoConnect={emailSessionReady ? autoConnectEmailWallet : false} onError={handleWalletError}>
                <WalletModalProvider>{children}</WalletModalProvider>
            </WalletProvider>
        </ConnectionProvider>
    );
};

export default WalletContextProvider;
